import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Avatar
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationsContext';
import {
  People,
  LocalHospital,
  TrendingUp,
  Warning,
  CalendarToday,
  AttachMoney,
  Speed,
  CloudDone,
  PersonAdd,
  MonitorHeart,
  BusinessCenter
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  Legend
} from 'recharts';
import axios from 'axios';
import { Select, MenuItem, FormControl, InputLabel, SelectChangeEvent } from '@mui/material';

import { SystemStats } from '../../types';

// Utility function to fix S3 URL format
const fixS3Url = (url: string | undefined): string | undefined => {
  if (!url) return undefined;

  // Check if it's an S3 URL with regional endpoint
  const s3RegionalPattern = /https:\/\/([^.]+)\.s3\.([^.]+)\.amazonaws\.com\/(.+)/;
  const match = url.match(s3RegionalPattern);

  if (match) {
    const bucketName = match[1];
    const path = match[3];

    // Convert to path-style URL (works better with CORS)
    const fixedUrl = `https://${bucketName}.s3.amazonaws.com/${path}`;
    console.log('🔧 Fixed S3 URL:', url, '->', fixedUrl);
    return fixedUrl;
  }

  // If already in standard format, return as is
  return url;
};

// Utility function to get cache-busted image URL
const getCacheBustedImageUrl = (url: string | undefined, timestamp?: number): string | undefined => {
  if (!url) return undefined;

  const fixedUrl = fixS3Url(url);
  if (!fixedUrl) return undefined;

  const ts = timestamp || Date.now();
  return `${fixedUrl}?t=${ts}`;
};

// Utility function to clean up names that have 'None' in them (from backend concatenation)
const cleanName = (name: string | undefined): string | undefined => {
  if (!name) return undefined;

  // Remove 'None' and extra spaces
  return name
    .replace(/\bNone\b/g, '') // Remove the word 'None'
    .replace(/\s+/g, ' ')     // Replace multiple spaces with single space
    .trim();                   // Remove leading/trailing spaces
};

export default function SuperAdminDashboard() {
  const { user } = useAuth();
  const { setRecentActivities: setNotificationActivities } = useNotifications();
  const navigate = useNavigate();
  const [stats, setStats] = useState<SystemStats>({
    totalPatients: 0,
    totalDoctors: 0,
    activeSubscriptions: 0,
    criticalAlerts: 0,
    todayAppointments: 0,
    monthlyRevenue: 0,
    systemUptime: 0,
    averageResponseTime: 0
  });

  const [totalMarketExecutives, setTotalMarketExecutives] = useState(0);
  const [pendingVerificationDoctors, setPendingVerificationDoctors] = useState(0);
  const [doctorsWithZeroPatients, setDoctorsWithZeroPatients] = useState(0);
  const [malePatients, setMalePatients] = useState(0);
  const [femalePatients, setFemalePatients] = useState(0);
  const [topExecutives, setTopExecutives] = useState<any[]>([]);

  const [recentActivities, setRecentActivities] = useState<any[]>([
    { id: 1, text: 'System initialized', time: 'Just now', type: 'system' }
  ]);

  // Analytics state
  const [weeklyTrendsData, setWeeklyTrendsData] = useState<any[]>([]);
  const [trendsFilter, setTrendsFilter] = useState<string>('heartrate');
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [trendsError, setTrendsError] = useState<string | null>(null);

  const monthlyData = [
    { name: 'Jan', patients: 120, appointments: 250 },
    { name: 'Feb', patients: 150, appointments: 320 },
    { name: 'Mar', patients: 180, appointments: 380 },
    { name: 'Apr', patients: 200, appointments: 450 },
    { name: 'May', patients: 250, appointments: 520 },
    { name: 'Jun', patients: 280, appointments: 600 }
  ];

  const systemStatsData = [
    { name: 'Doctors', value: stats.totalDoctors, icon: <LocalHospital />, color: '#0088FE' },
    { name: 'Patients', value: stats.totalPatients, icon: <People />, color: '#00C49F' },
    { name: 'Appointments', value: stats.todayAppointments, icon: <CalendarToday />, color: '#FFBB28' }
  ];



  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem('token');
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };

        // Log request details for debugging
        console.log('🔍 Fetching Dashboard Data...');
        console.log('👤 User:', user);
        console.log('🔑 Token:', token ? 'Present' : 'Missing');

        // Define promises
        // Use /health/dashboard for vitals data (for critical alerts)
        const healthDashboardUrl = `${process.env.REACT_APP_API_URL}/health/dashboard`;
        console.log('REQUEST: ', healthDashboardUrl);
        const healthDashboardPromise = axios.get(healthDashboardUrl, config);

        // Also fetch /patients for complete patient data including gender
        const patientsUrl = `${process.env.REACT_APP_API_URL}/patients`;
        console.log('REQUEST: ', patientsUrl);
        const patientsPromise = axios.get(patientsUrl, config);

        const doctorsPromise = axios.get(`${process.env.REACT_APP_API_URL}/auth/doctors`);

        // SuperAdmin should see ALL appointments, not just their own
        const appointmentsUrl = user?.role === 'superadmin'
          ? `${process.env.REACT_APP_API_URL}/appointments/all`
          : `${process.env.REACT_APP_API_URL}/appointments/${user?.role}/${user?.id}`;
        console.log('REQUEST: ', appointmentsUrl);
        const appointmentsPromise = axios.get(appointmentsUrl, config);

        const executivesPromise = axios.get(`${process.env.REACT_APP_API_URL}/auth/marketingusers`);

        // NEW: Fetch executive performance
        const performanceDashboardUrl = `${process.env.REACT_APP_API_URL}/marketing/executive-performance`;
        console.log('REQUEST: ', performanceDashboardUrl);
        const performancePromise = axios.get(performanceDashboardUrl, config);

        // Execute all promises
        const results = await Promise.allSettled([
          healthDashboardPromise,
          patientsPromise,
          doctorsPromise,
          appointmentsPromise,
          executivesPromise,
          performancePromise
        ]);

        // Process results
        const healthDashboardRes = results[0];
        const patientsRes = results[1];
        const doctorsRes = results[2];
        const appointmentsRes = results[3];
        const executivesRes = results[4];
        const performanceRes = results[5];

        console.log('📊 API Results Status:', {
          healthDashboard: healthDashboardRes.status,
          patients: patientsRes.status,
          doctors: doctorsRes.status,
          appointments: appointmentsRes.status,
          executives: executivesRes.status,
          performance: performanceRes.status
        });

        if (healthDashboardRes.status === 'rejected') console.error('❌ Health Dashboard API Error:', healthDashboardRes.reason);
        if (patientsRes.status === 'rejected') console.error('❌ Patients API Error:', patientsRes.reason);
        if (doctorsRes.status === 'rejected') console.error('❌ Doctors API Error:', doctorsRes.reason);
        if (appointmentsRes.status === 'rejected') console.error('❌ Appointments API Error:', appointmentsRes.reason);
        if (executivesRes.status === 'rejected') console.error('❌ Executives API Error:', executivesRes.reason);
        if (performanceRes.status === 'rejected') console.error('❌ Executive Performance API Error:', performanceRes.reason);

        // Extract data or use empty array/mock on failure
        // Health dashboard data for vitals (critical alerts)
        let patientsWithVitals = [];
        if (healthDashboardRes.status === 'fulfilled') {
          const pData = healthDashboardRes.value.data;
          patientsWithVitals = Array.isArray(pData) ? pData : (pData.patients || pData.patients_with_data || []);
        }

        // Complete patient data for gender distribution
        let patients = [];
        if (patientsRes.status === 'fulfilled') {
          const pData = patientsRes.value.data;
          patients = Array.isArray(pData) ? pData : [];
        }

        const doctors = doctorsRes.status === 'fulfilled' ? doctorsRes.value.data : [];
        const appointments = appointmentsRes.status === 'fulfilled' ? appointmentsRes.value.data : [];
        const executives = executivesRes.status === 'fulfilled' ? executivesRes.value.data : [];

        // Helper to check if a date string is today
        const isToday = (dateString: string) => {
          if (!dateString) return false;
          const today = new Date();
          const date = new Date(dateString);
          return (
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear()
          );
        };

        console.log('📅 Debugging Appointment Dates:');
        appointments.forEach((a: any) => {
          console.log(` - ID ${a.id}: Date=${a.appointment_date}, IsToday=${isToday(a.appointment_date)}`);
        });

        // --- Status Calculation Logic (Mirrored from HealthMonitorPage) ---
        const calculateHeartRateStatus = (bpm: number | null): string | null => {
          if (bpm === null) return null;
          if (bpm < 60) return 'Slow';
          if (bpm <= 80) return 'Normal';
          if (bpm <= 100) return 'Elevated';
          if (bpm <= 120) return 'High';
          return 'Very High';
        };

        const calculateBPStatus = (systolic: number | null, diastolic: number | null): string | null => {
          if (!systolic || !diastolic) return null;
          if (systolic > 180 || diastolic > 120) return 'Hypertensive Crisis';
          if (systolic >= 140 || diastolic >= 90) return 'Stage 2 Hypertension';
          if ((systolic >= 130 && systolic <= 139) || (diastolic >= 80 && diastolic <= 89)) return 'Stage 1 Hypertension';
          if ((systolic >= 120 && systolic <= 129) && diastolic < 80) return 'Elevated';
          return 'Normal';
        };

        const calculateBMIStatus = (bmi: number | null): string | null => {
          if (bmi === null) return null;
          if (bmi < 18.5) return 'Underweight';
          if (bmi <= 24.9) return 'Normal';
          if (bmi <= 29.9) return 'Overweight';
          return 'Obese';
        };

        const mapStatusToCategory = (status: string | null): 'critical' | 'warning' | 'normal' => {
          if (!status) return 'normal';
          const s = status.toLowerCase();
          if (['high', 'very high', 'stage 2 hypertension', 'hypertensive crisis', 'obese', 'critical'].includes(s)) return 'critical';
          if (['slow', 'elevated', 'stage 1 hypertension', 'underweight', 'overweight', 'warning'].includes(s)) return 'warning';
          return 'normal';
        };

        // Recalculate status locally using vitals data
        const processedPatients = Array.isArray(patientsWithVitals) ? patientsWithVitals.map((p: any) => {
          const vitalsObj = p.latest_vitals || {};
          const heartData = vitalsObj.heartrate || vitalsObj.heart_rate || vitalsObj.heartRate || {};
          const bpData = vitalsObj.bp || {};
          const bmiData = vitalsObj.bmi || {};

          const heartRateVal = parseInt(heartData.heart_rate) || null;
          const systolicVal = parseInt(bpData.systolic_bp) || null;
          const diastolicVal = parseInt(bpData.diastolic_bp) || null;
          const bmiVal = parseFloat(bmiData.bmi) || null;

          const hrStatusDet = heartRateVal !== null ? calculateHeartRateStatus(heartRateVal) : null;
          const bpStatusDet = (systolicVal && diastolicVal) ? calculateBPStatus(systolicVal, diastolicVal) : null;
          const bmiStatusDet = bmiVal !== null ? calculateBMIStatus(bmiVal) : null;

          const hrCat = mapStatusToCategory(hrStatusDet);
          const bpCat = mapStatusToCategory(bpStatusDet);
          const bmiCat = mapStatusToCategory(bmiStatusDet);

          let overall: 'critical' | 'warning' | 'normal' = 'normal';
          if ([hrCat, bpCat, bmiCat].includes('critical')) overall = 'critical';
          else if ([hrCat, bpCat, bmiCat].includes('warning')) overall = 'warning';

          return { ...p, healthStatus: overall };
        }) : [];

        // Calculate Stats
        const activeDoctors = Array.isArray(doctors) ? doctors.filter((d: any) => d.is_active).length : 0;
        const criticalPatients = processedPatients.filter((p: any) => p.healthStatus === 'critical').length;

        const todayAptsCount = Array.isArray(appointments) ? appointments.filter((a: any) =>
          isToday(a.appointment_date)
        ).length : 0;

        // Calculate pending verification doctors (is_verified = false)
        const pendingDoctors = Array.isArray(doctors) ? doctors.filter((d: any) => !d.is_verified).length : 0;

        // Calculate doctors with 0 active patients
        const doctorsWithNoPatients = Array.isArray(doctors) ? doctors.filter((d: any) => (d.patients_count === 0 || !d.patients_count)).length : 0;

        // Calculate male and female patient counts
        const maleCount = Array.isArray(patients) ? patients.filter((p: any) => p.gender?.toLowerCase() === 'male').length : 0;
        const femaleCount = Array.isArray(patients) ? patients.filter((p: any) => p.gender?.toLowerCase() === 'female').length : 0;

        console.log('👥 Gender Distribution:', {
          total: patients.length,
          male: maleCount,
          female: femaleCount,
          sampleGenders: patients.slice(0, 5).map((p: any) => ({ name: p.name, gender: p.gender }))
        });

        setStats({
          totalPatients: Array.isArray(patients) ? patients.length : 0,
          totalDoctors: activeDoctors,
          activeSubscriptions: 1180, // Mock for now
          criticalAlerts: criticalPatients,
          todayAppointments: todayAptsCount,
          monthlyRevenue: 82500, // Mock for now
          systemUptime: 99.9, // Mock for now
          averageResponseTime: 1.2 // Mock for now
        });

        const totalExecutives = Array.isArray(executives) ? executives.length : 0;
        setTotalMarketExecutives(totalExecutives);
        setPendingVerificationDoctors(pendingDoctors);
        setDoctorsWithZeroPatients(doctorsWithNoPatients);
        setMalePatients(maleCount);
        setFemalePatients(femaleCount);

        // Process Executive Performance Data
        let sortedExecutives = [];
        if (performanceRes.status === 'fulfilled') {
          // API returns [{ executive_name: "Name", total_joins: 20 }, ...]
          // Map to UI expected format: { full_name: "Name", active_subscribers: 20 }
          console.log('🔍 Exec Performance Raw Data (NEW):', performanceRes.value.data);
          const mappedData = Array.isArray(performanceRes.value.data)
            ? performanceRes.value.data.map((item: any, index: number) => {
              console.log(`  - Item ${index}:`, item);
              return {
                full_name: item.executive_name || item.full_name || 'Unknown Executive',
                active_subscribers: item.total_joins || item.active_subscribers || 0,
                id: item.id || `exec-${index}-${Date.now()}` // Ensure key exists
              };
            })
            : [];

          // Sort by active subscribers descending
          sortedExecutives = mappedData.sort((a: any, b: any) => b.active_subscribers - a.active_subscribers).slice(0, 3);
        } else {
          // Fallback to previous logic if API fails or for initial load before API is ready
          const executivesWithSubscribers = Array.isArray(executives)
            ? executives.map((e: any, index: number) => ({
              ...e,
              // Generate sample subscriber counts if not present
              active_subscribers: e.active_subscribers || Math.floor(Math.random() * 150) + 50
            }))
            : [];

          sortedExecutives = executivesWithSubscribers
            .sort((a: any, b: any) => (b.active_subscribers || 0) - (a.active_subscribers || 0))
            .slice(0, 3);
        }

        setTopExecutives(sortedExecutives);

        console.log('👔 Top Executives:', sortedExecutives);

        console.log('📊 Dashboard Stats Updated:', {
          totalPatients: patients.length,
          malePatients: maleCount,
          femalePatients: femaleCount,
          doctorsWithNoPatients: doctorsWithNoPatients
        });

        // Generate Recent Activities
        // Combine latest patients, doctors, and appointments
        // Helper function to parse timestamps correctly (API returns UTC without 'Z')
        const parseTimestamp = (timestamp: string) => {
          if (!timestamp) return new Date();
          // If timestamp doesn't end with 'Z', append it to treat as UTC
          const ts = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
          return new Date(ts);
        };

        // Helper to check if a date is within the last 7 days
        const isWithinLastWeek = (dateString: string) => {
          if (!dateString) return false;
          const date = parseTimestamp(dateString);
          const now = new Date();
          const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return date >= sevenDaysAgo && date <= now;
        };

        const newPatients = Array.isArray(patients) ? patients
          .filter((p: any) => p.created_at && isWithinLastWeek(p.created_at)) // Only patients from last 7 days
          .map((p: any) => ({
            id: `p-${p.id}`,
            text: `Dr. ${cleanName(p.doctor_name) || 'Unknown'} created patient: ${cleanName(p.name) || 'Unknown Patient'}`,
            time: parseTimestamp(p.created_at),
            type: 'patient',
            entityId: p.id,
            navigateTo: '/patients'
          })) : [];

        const newDoctors = Array.isArray(doctors) ? doctors
          .filter((d: any) => d.created_at) // Only include if created_at exists
          .map((d: any) => ({
            id: `d-${d.id}`,
            text: `Dr. ${cleanName(d.full_name) || d.username} joined the system`,
            time: parseTimestamp(d.created_at),
            type: 'doctor',
            entityId: d.id,
            navigateTo: '/doctors'
          })) : [];

        const newAppointments = Array.isArray(appointments) ? appointments
          .filter((a: any) => a.created_at || a.createdAt) // Only include if timestamp exists
          .map((a: any) => ({
            id: `a-${a.id}`,
            text: `Dr. ${cleanName(a.doctor_name || a.doctorName) || 'Unknown'} created appointment for ${cleanName(a.patientName || a.patient_name) || 'Patient'}`,
            time: parseTimestamp(a.created_at || a.createdAt),
            type: 'appointment',
            entityId: a.id,
            navigateTo: '/appointments'
          })) : [];

        const criticalAlertsLog = processedPatients
          .filter((p: any) => p.healthStatus === 'critical')
          .map((p: any) => ({
            id: `c-${p.id}`,
            text: `Critical alert: Patient ${cleanName(p.name) || 'Unknown'} - Status Critical`,
            time: new Date(), // Real-time alert, so current time
            type: 'critical',
            entityId: p.id,
            navigateTo: '/patients'
          }));

        console.log('🔍 Activity Filtering Debug:');
        console.log('  - Patients with created_at:', patients.filter((p: any) => p.created_at).length, '/', patients.length);
        console.log('  - Doctors with created_at:', doctors.filter((d: any) => d.created_at).length, '/', doctors.length);
        console.log('  - Appointments with created_at:', appointments.filter((a: any) => a.created_at || a.createdAt).length, '/', appointments.length);

        // Debug timezone issues
        if (patients.length > 0 && patients[0].created_at) {
          console.log('🕐 Timezone Debug:');
          console.log('  - Current time:', new Date().toISOString());
          console.log('  - Sample patient created_at (raw):', patients[0].created_at);
          console.log('  - Sample patient created_at (parsed):', new Date(patients[0].created_at).toISOString());
          console.log('  - Sample patient created_at (local):', new Date(patients[0].created_at).toString());
        }

        // Merge and sort by time (descending)
        // Removed criticalAlertsLog as per user request to reduce noise
        const allActivitiesBeforeSlice = [...newPatients, ...newDoctors, ...newAppointments]
          .sort((a: any, b: any) => b.time.getTime() - a.time.getTime());

        console.log('📋 Recent Activities Debug (BEFORE SLICE):');
        console.log('  - New Patients:', newPatients.length);
        console.log('  - New Doctors:', newDoctors.length);
        console.log('  - New Appointments:', newAppointments.length);
        console.log('  - Total Activities (before sorting):', [...newPatients, ...newDoctors, ...newAppointments].length);
        console.log('  - All Activities (sorted, before slice):');
        allActivitiesBeforeSlice.forEach((activity, index) => {
          console.log(`    ${index + 1}. [${activity.type}] ${activity.text} - Time: ${activity.time.toISOString()}`);
        });

        const allActivities = allActivitiesBeforeSlice
          .slice(0, 10) // Take top 10 instead of 5
          .map((activity: any) => {
            const activityDate = activity.time;
            const now = new Date();
            const diffMs = now.getTime() - activityDate.getTime();
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            let relativeTime = '';
            if (diffMins < 1) {
              relativeTime = 'Just now';
            } else if (diffMins < 60) {
              relativeTime = `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
            } else if (diffHours < 24) {
              relativeTime = `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
            } else {
              relativeTime = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
            }

            return {
              ...activity,
              time: relativeTime
            };
          });

        console.log('📋 Recent Activities Debug (AFTER SLICE):');
        console.log('  - Final Activities (top 10):', allActivities);
        console.log('  - Activity Details:');
        allActivities.forEach((activity, index) => {
          console.log(`    ${index + 1}. [${activity.type}] ${activity.text} - ${activity.time}`);
        });

        if (allActivities.length > 0) {
          // @ts-ignore
          setRecentActivities(allActivities);
          setNotificationActivities(allActivities); // Update notifications context
        } else {
          // Keep default or set empty
          setRecentActivities([{ id: 1, text: 'No recent activities', time: 'Just now', type: 'system' }]);
          setNotificationActivities([]);
        }

      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      }
    };

    if (user) {
      // Initial fetch
      console.log('🚀 SuperAdmin Dashboard: Initial data fetch');
      fetchDashboardData();

      // Set up polling interval for real-time updates (10 seconds)
      const pollingInterval = setInterval(() => {
        console.log('🔄 SuperAdmin Dashboard: Polling data refresh');
        fetchDashboardData();
      }, 10000); // 10 second interval

      // Cleanup function to clear interval when component unmounts
      return () => {
        console.log('🛑 SuperAdmin Dashboard: Cleaning up polling interval');
        clearInterval(pollingInterval);
      };
    }
  }, [user]);

  // Fetch Weekly Trends Data
  useEffect(() => {
    const fetchTrendsData = async () => {
      // Logic only runs if we have a user (and ideally a token)
      if (!user) return;

      try {
        setTrendsLoading(true);
        setTrendsError(null);
        const token = localStorage.getItem('token');
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };

        const trendsRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/dashboard/admin-weekly-health-trends?days=7&activity_status=${trendsFilter}`,
          config
        );

        const trendsRaw = trendsRes.data;
        // Handle different possible response structures
        let trendsList = [];
        if (Array.isArray(trendsRaw)) {
          trendsList = trendsRaw;
        } else if (trendsRaw.trends && Array.isArray(trendsRaw.trends)) {
          trendsList = trendsRaw.trends;
        } else if (trendsRaw.data && Array.isArray(trendsRaw.data)) {
          trendsList = trendsRaw.data;
        }

        // Helper for mapping status
        const mapStatusToCategory = (status: string | null): 'critical' | 'warning' | 'normal' => {
          if (!status) return 'normal';
          const s = status.toLowerCase();
          if (['high', 'very high', 'stage 2 hypertension', 'hypertensive crisis', 'obese', 'critical'].includes(s)) return 'critical';
          if (['slow', 'elevated', 'stage 1 hypertension', 'underweight', 'overweight', 'warning'].includes(s)) return 'warning';
          return 'normal';
        };

        const formattedTrendsData = [];
        // Generate last 7 days
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          const dateStr = date.toISOString().split('T')[0];

          // Filter matching trends for this date
          const matchingTrends = trendsList.filter((t: any) => {
            if (!t.date) return false;
            // Handle date formats like YYYY-MM-DD or ISO
            const tDate = t.date.includes('T') ? t.date.split('T')[0] : t.date;
            return tDate === dateStr;
          });

          const counts = { critical: 0, warning: 0, normal: 0 };

          // Aggregate all status entries for this date
          matchingTrends.forEach((t: any) => {
            Object.entries(t).forEach(([key, value]) => {
              if (key !== 'date' && typeof value === 'number') {
                const category = mapStatusToCategory(key);
                counts[category] += value;
              }
            });
          });

          formattedTrendsData.push({
            date: dateStr,
            ...counts
          });
        }

        console.log('Weekly Trends Data:', formattedTrendsData);
        setWeeklyTrendsData(formattedTrendsData);
      } catch (error: any) {
        console.error('Error fetching trends data:', error);
        setTrendsError(error.response?.data?.detail || 'Failed to load trends data');
        setWeeklyTrendsData([]);
      } finally {
        setTrendsLoading(false);
      }
    };

    fetchTrendsData();
  }, [user, trendsFilter]);


  // Handle activity click to navigate to the relevant page
  const handleActivityClick = (activity: any) => {
    if (activity.navigateTo) {
      console.log(`🔗 Navigating to ${activity.navigateTo} for ${activity.type} activity with entityId: ${activity.entityId}`);
      navigate(activity.navigateTo, {
        state: { highlightId: activity.entityId }
      });
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle, titleVariant = 'h6', fontWeight = 'bold' }: any) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant={titleVariant} sx={{ fontWeight }}>
              {title}
            </Typography>
            <Typography variant="h4" component="h2" sx={{ color }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ color, opacity: 0.8 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        Super Admin Dashboard
      </Typography>

      {/* Admin Profile */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar
              src={getCacheBustedImageUrl(user?.profile_image)}
              imgProps={{
                crossOrigin: "anonymous",
                onError: (e) => {
                  console.error('❌ Admin avatar image failed to load:', user?.profile_image);
                  console.error('Error event:', e);
                  console.error('This is likely a CORS or S3 permissions issue');
                  // Try to fetch the image to get more details
                  fetch(user?.profile_image || '', { mode: 'no-cors' })
                    .then(() => console.log('✅ Image exists but CORS is blocking it'))
                    .catch(err => console.error('❌ Image fetch error:', err));
                }
              }}
              sx={{
                width: 80,
                height: 80,
                bgcolor: user?.profile_image ? 'transparent' : 'primary.main',
                fontSize: '2rem',
                border: user?.profile_image ? '2px solid #e0e0e0' : '2px solid',
                borderColor: user?.profile_image ? 'primary.light' : 'primary.light'
              }}
            >
              {(!user?.profile_image && (user?.full_name || user?.username)?.charAt(0)?.toUpperCase()) || 'S'}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                Welcome, {user?.full_name || user?.username}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Super Administrator • System Overview
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Alerts */}
      {stats.criticalAlerts > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          You have {stats.criticalAlerts} trends flagged for review!
        </Alert>
      )}

      {/* Key Metrics */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)'
          },
          gap: 3,
          mb: 4
        }}
      >
        <StatCard
          title="Total Patients"
          value={stats.totalPatients.toLocaleString()}
          icon={<People fontSize="large" />}
          color="primary.main"
          subtitle="+12% from last month"
        />
        <StatCard
          title="Active Doctors"
          value={stats.totalDoctors}
          icon={<LocalHospital fontSize="large" />}
          color="success.main"
          subtitle="All verified"
        />
        <StatCard
          title="Market Executives"
          value={totalMarketExecutives}
          icon={<BusinessCenter fontSize="large" />}
          color="info.main"
          subtitle="Total executives"
        />
        <StatCard
          title="Trends Flagged"
          value={stats.criticalAlerts}
          icon={<Warning fontSize="large" />}
          color="error.main"
          subtitle="Flagged for review"
        />
        <StatCard
          title="Today's Appointments"
          value={stats.todayAppointments}
          icon={<CalendarToday fontSize="large" />}
          color="warning.main"
        />
        <StatCard
          title="Pending Verification Doctors"
          value={pendingVerificationDoctors}
          icon={<LocalHospital fontSize="large" />}
          color="warning.main"
          subtitle="Awaiting verification"
        />
        <StatCard
          title="Doctors with No Active Patients"
          titleVariant="h6"
          fontWeight="bold"
          value={doctorsWithZeroPatients}
          icon={<LocalHospital fontSize="large" />}
          color="info.main"
          subtitle="No assigned patients"
        />
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom variant="h6">
                  Patient Gender Distribution
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ minWidth: 60 }}>
                      Male:
                    </Typography>
                    <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                      {malePatients}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ minWidth: 60 }}>
                      Female:
                    </Typography>
                    <Typography variant="h5" sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
                      {femalePatients}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ color: 'success.main', opacity: 0.8 }}>
                <People fontSize="large" />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
      {/* Charts and Activities */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: '2fr 1fr'
          },
          gap: 3,
          mb: 4
        }}
      >


        {/* Top 3 Executives */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box sx={{ width: '100%' }}>
                <Typography color="textSecondary" gutterBottom variant="h6" sx={{ fontWeight: 'bold' }}>
                  Top 3 Executives
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
                  By active subscribers
                </Typography>
                {topExecutives.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {topExecutives.map((exec: any, index: number) => (
                      <Box
                        key={exec.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1,
                          borderRadius: 1,
                          bgcolor: index === 0 ? 'primary.light' : 'action.hover',
                          border: index === 0 ? '1px solid' : 'none',
                          borderColor: index === 0 ? 'primary.main' : 'transparent'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: 'primary.main' }}>
                            {index + 1}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: index === 0 ? 'bold' : 'medium' }}>
                            {exec.full_name || exec.username || 'Unknown'}
                          </Typography>
                        </Box>
                        <Chip
                          label={`${exec.active_subscribers || 0}`}
                          size="small"
                          color={index === 0 ? 'primary' : 'default'}
                          sx={{ fontWeight: 'bold', minWidth: 45 }}
                        />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    No executives data available
                  </Typography>
                )}
              </Box>
              <Box sx={{ color: 'warning.main', opacity: 0.8, ml: 1 }}>
                <BusinessCenter fontSize="large" />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* System Statistics */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              System Overview
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {systemStatsData.map((stat, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ color: stat.color, display: 'flex' }}>
                      {stat.icon}
                    </Box>
                    <Typography variant="h6">{stat.name}</Typography>
                  </Box>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: stat.color }}>
                    {stat.value.toLocaleString()}
                  </Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: '2fr 1fr'
          },
          gap: 3
        }}
      >
        {/* Health Status Trends */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Weekly Health Status Overview
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="trends-filter-label">Vital Type</InputLabel>
                <Select
                  labelId="trends-filter-label"
                  value={trendsFilter}
                  label="Vital Type"
                  onChange={(e: SelectChangeEvent) => setTrendsFilter(e.target.value as string)}
                >
                  <MenuItem value="heartrate">Heart Rate</MenuItem>
                  <MenuItem value="bp">Blood Pressure</MenuItem>
                  <MenuItem value="bmi">BMI Status</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {trendsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Typography>Loading trends...</Typography>
              </Box>
            ) : trendsError ? (
              <Alert severity="error" sx={{ my: 2 }}>
                {trendsError}
              </Alert>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={weeklyTrendsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="critical" stackId="a" fill="#f44336" name="Critical" />
                  <Bar dataKey="warning" stackId="a" fill="#ff9800" name="Warning" />
                  <Bar dataKey="normal" stackId="a" fill="#4caf50" name="Normal" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* New Patients Added */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              New Patients Added This Week
            </Typography>
            <Box
              sx={{
                maxHeight: 400,
                overflowY: 'auto',
                overflowX: 'hidden',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.3)',
                  },
                },
              }}
            >
              <List>
                {recentActivities
                  .filter((activity) => activity.type === 'patient')
                  .map((activity) => (
                    <ListItem
                      key={activity.id}
                      divider
                      onClick={() => handleActivityClick(activity)}
                      sx={{
                        cursor: activity.navigateTo ? 'pointer' : 'default',
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          backgroundColor: activity.navigateTo ? 'action.hover' : 'transparent'
                        }
                      }}
                    >
                      <ListItemIcon>
                        <PersonAdd color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={activity.text}
                        secondary={activity.time}
                      />
                    </ListItem>
                  ))}
                {recentActivities.filter((activity) => activity.type === 'patient').length === 0 && (
                  <ListItem>
                    <ListItemText
                      primary="No new patients added this week"
                      secondary="Patients added in the last 7 days will appear here"
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}